import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlightDetails, FlightStatus, Rules } from '../interface/flight.details';
import moment from 'moment';
import { RemarkToCodeMapping, TripType, DisplayType, CheckInType } from '@industronics/fids-utils';
import { filterPosAviationLangs, filterPosAviationStationLangs, isPosAviationHiddenRemark, normalizeCode, toCamelCase } from '../utils/common.utils';
import { Template, TemplateColumn } from '../interface/template';
import { getData } from '../utils/api';
import { apiRoutes } from '../utils/apiRoutes';
import { AdvertisementDetail, Announcement, Cluster } from '../interface/device.details';
import { useSocket } from '../context/SocketContext';
import RemarksRotator from './RemarksRotator';
import StationsRotator from './StationsRotator';
import AdvertisementDisplay from './AdvertisementDisplay';
import { useAdSequence } from '../hooks/useAdSequence';
import AnnouncementRenderer from './announcements/AnnouncementRenderer';
import { CARGO_SERVICE_TYPE_CODES } from '../utils/constants';
import AirlineCell from './AirlineCell';
import FlightNumberCell from './FlightNumberCell';
import { ADS_URL } from '../utils/env-utils';

interface TemplateComponentProps {
  cluster: Cluster;
  template: Template,
  rowsPerPage: number;
  footerHeight?: number;
  tableHeaderHeight?: number;
  deviceId?: string;
  sequence: number
  flip: number;
  isChild?: boolean;
  isLastChild?: boolean;
  totalDevices: number;
  displayLayoutsOrder: string[];
  isMatrix?: boolean;
  startIncrement?: number;
  endIncrement?: number;
  deviceIndex?: number;
  enableFooter?: boolean;
  advertisement?: AdvertisementDetail;
  headerHeight?: number;
  rowHeight?: number;
  announcement?: Announcement;
  flipOffsetAtSeconds: number;
  fontSize: string;
  onBackward?: () => void
}

const TemplateComponent: React.FC<TemplateComponentProps> = ({ fontSize, flipOffsetAtSeconds, announcement, headerHeight, tableHeaderHeight, rowHeight, onBackward, footerHeight, advertisement, enableFooter, startIncrement = 0, endIncrement = 0, deviceIndex = 0, isMatrix, displayLayoutsOrder, totalDevices, isLastChild, isChild, sequence, flip, cluster, template, rowsPerPage }) => {

  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [rules, setRules] = useState<Rules[]>([]);
  const displayOffset = 20;
  const timezone = process.env.REACT_APP_TIMEZONE || 8;
  const [currentPage, setCurrentPage] = useState(sequence);
  const [flipIndex, setFlipIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const { socket } = useSocket();
  const [currentFlights, setCurrentFlights] = useState<FlightDetails[]>([]);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [endIndex, setEndIndex] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState(moment());
  const globalStartTime = useRef(performance.now()).current;
  const [logoSize, setLogoSize] = useState({ width: 180, height: 45 });
  const [screenWidth, setScreenWidth] = useState(0);
  const [remarkOffsets, setRemarkOffsets] = useState<Record<string, number>>({});
  const [isDataChanging, setIsDataChanging] = useState(false);
  const prevDisplayedFlightsRef = useRef<FlightDetails[]>([]);
  const prevFlipIndexRef = useRef(0);

  const effectiveAdvertisement = useMemo(() => {
    if (!advertisement) return advertisement;
    // If this device/page has no flights to show, kill flightDuration so ads run without "flight filler"
    if (!currentFlights || currentFlights.length === 0) {
      return { ...advertisement, flightDuration: 0 };
    }
    return advertisement;
  }, [advertisement, currentFlights?.length]);

  const { currentAd, isRecurring } = useAdSequence(effectiveAdvertisement);

  // Determine if we should show only ads (no flight UI)
  const hasNoFlights = !currentFlights || currentFlights.length === 0;
  const isFullScreenAd = currentAd?.advertisementType === 'FullScreen' && !currentAd?.ratio;
  const isRatioAd = currentAd?.ratio && currentAd?.ratio > 0;

  // Scenario 1: No flights + FullScreen ad = Show only ad
  const showOnlyAd = hasNoFlights && isFullScreenAd && currentAd;

  // Scenario 2: No flights + (Ratio ad OR custom default image) = Show default UI (only after initial fetch)
  const showDefaultUI = hasNoFlights && initialLoadDone && ((isRatioAd && currentAd) || cluster?.settings?.defaultDisplayImage);


  // Inline JSX for no-flights UI — NOT a nested component to avoid unmount/remount on every parent re-render
  const defaultNoFlightsUI = showDefaultUI ? (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#1a1a1a',
      color: 'white',
      fontSize: fontSize
    }}>
      {cluster?.settings?.defaultDisplayImage ? (
        <div style={{
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          paddingBottom: enableFooter ? footerHeight : 0,
        }}>
          <img
            src={`${ADS_URL}/cluster-settings/${cluster.settings.defaultDisplayImage}`}
            alt="Default display"
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2 style={{ fontSize: `calc(${fontSize} * 2)`, marginBottom: '1rem' }}>
            No Flights Available
          </h2>
          <p style={{ fontSize: `calc(${fontSize} * 1.2)`, opacity: 0.8 }}>
            There are currently no scheduled flights to display
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="font-semibold fix-bottom" style={{
        position: 'absolute',
        bottom: 0,
        width: '100%',
        height: footerHeight
      }}>
        {(!isMatrix && enableFooter || (isMatrix && isLastChild)) && (
          <div>
            <AnnouncementRenderer
              announcement={announcement}
              footerHeight={footerHeight}
            />
          </div>
        )}
      </div>
    </div>
  ) : null;

  const defaultOffsets: Record<string, number> = {
    [RemarkToCodeMapping.CANCELLED]: 20,
    [RemarkToCodeMapping.DEPARTED]: 20,
    [RemarkToCodeMapping.CAROUSEL_CLOSED]: 20,
  };

  const getOffsetIfRemovable = (remarkKey?: string) => {
    if (!remarkKey) return undefined;
    if (remarkOffsets[remarkKey] != null) return remarkOffsets[remarkKey];
    if (defaultOffsets[remarkKey] != null) return defaultOffsets[remarkKey];
    return undefined; // not removable => keep
  };

  const fetchRemovalOffsets = async () => {
    try {
      const res = await getData<any>(apiRoutes.flightRemovalOffsets.getForDevice);
      if (res?.success && Array.isArray(res.data)) {
        const map: Record<string, number> = {};
        res.data.forEach((x: any) => {
          if (x?.remarkKey && Number.isFinite(x?.offsetMinutes)) {
            map[x.remarkKey] = x.offsetMinutes;
          }
        });
        setRemarkOffsets(map);
      }
    } catch (e) {
      console.error("Failed to fetch removal offsets", e);
    }
  };

  useEffect(() => {
    fetchRemovalOffsets();
  }, []);


  useEffect(() => {
    if (!socket) return;

    const onFlightRemovalOffsetUpdate = () => {
      console.log("flightRemovalOffset")
      fetchRemovalOffsets();
    };

    socket.on("flightRemovalOffset:update", onFlightRemovalOffsetUpdate);

    return () => {
      socket.off("flightRemovalOffset:update", onFlightRemovalOffsetUpdate);
    };
  }, [socket, fetchRemovalOffsets]);


  useEffect(() => {
    if (!socket) return;

    const handleFlightUpdate = (flightData: FlightDetails) => {
      setFlights((prevFlights) => {
        const now = Date.now();
        const shouldKeep = shouldKeepFlight(flightData, now);
        const flightIndex = prevFlights.findIndex(
          (flight) => flight._id === flightData._id
        );

        let updatedFlights = [...prevFlights];

        if (!shouldKeep) {
          if (flightIndex === -1) return prevFlights;
          updatedFlights.splice(flightIndex, 1);
        } else if (flightIndex !== -1) {
          const prev = updatedFlights[flightIndex];
          updatedFlights[flightIndex] = {
            ...flightData,
            standCodeChanged: flightData.standCode !== prev.standCode,
          };
        } else {
          updatedFlights.push(flightData);
        }

        updatedFlights = updatedFlights
          .filter((x) => shouldKeepFlight(x, now))
          .sort((a, b) => {
            const tA = a._cachedSortTime ?? (a._cachedSortTime = new Date(a.scheduledTravelTime).getTime());
            const tB = b._cachedSortTime ?? (b._cachedSortTime = new Date(b.scheduledTravelTime).getTime());

            if (tA !== tB) return tA - tB;

            const idA = a._id;
            const idB = b._id;

            if (typeof idA === 'string' && typeof idB === 'string') {
              return idA.localeCompare(idB);
            }

            return String(idA).localeCompare(String(idB));
          });

        return updatedFlights;
      });
    };

    const addFlightUpdateListener = () => {
      socket.off("flight.update", handleFlightUpdate);
      socket.on("flight.update", handleFlightUpdate);
    };

    const handleConnect = () => {
      addFlightUpdateListener();
      fetchFlights();
    };

    socket.on("connect", handleConnect);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("flight.update", handleFlightUpdate);
    };
  }, [socket]);


  // Non-destructive time-window filter — flights reappear automatically when they enter the window
  const windowedFlights = useMemo(() => {
    const timeWindowHours = cluster?.settings?.flightTimeWindowInHour;
    if (timeWindowHours == null || timeWindowHours <= 0) return flights;

    const cutoff = currentTime.valueOf() + timeWindowHours * 60 * 60 * 1000;
    return flights.filter(f => {
      const scheduledMs = new Date(f.scheduledTravelTime).getTime();
      return !Number.isFinite(scheduledMs) || scheduledMs <= cutoff;
    });
  }, [flights, currentTime, cluster?.settings?.flightTimeWindowInHour]);

  useEffect(() => {
    if (!displayLayoutsOrder) return;

    if (!windowedFlights.length) {
      setCurrentFlights([]);
      return;
    }

    // Determine if this is a matrix or linear array
    const isMatrix = Array.isArray(displayLayoutsOrder?.[0]);

    // ✅ Identify group end indexes (footer devices)
    const groupEndFlatIndexes = (() => {
      if (!isMatrix) return [totalDevices - 1];
      const groups = displayLayoutsOrder as any[];
      let acc = -1;
      return groups.map((g) => {
        acc += g.length;
        return acc;
      });
    })();

    // Calculate total extra rows per cycle (only for matrix)
    const totalExtraRowsPerCycle = (() => {
      if (!isMatrix) return 0;

      const groups = displayLayoutsOrder as any[];
      let total = 0;

      groups.forEach(row => {
        const middleDevices = Math.max(0, row.length - 2);
        total += middleDevices * 2;

        if (row.length > 0) {
          total += 2;
        }
      });

      return total;
    })();

    let startIdx, endIdx;

    if (isMatrix) {
      // ========== MATRIX LOGIC ==========
      const footerCountPerCycle = enableFooter ? groupEndFlatIndexes.length : 0;
      const cycleSize = (totalDevices * rowsPerPage) + totalExtraRowsPerCycle - footerCountPerCycle;

      const footersBeforeMe = enableFooter
        ? groupEndFlatIndexes.filter((endIdx) => endIdx < deviceIndex).length
        : 0;

      const baseCycleOffset = flipIndex * cycleSize;

      startIdx =
        baseCycleOffset +
        (deviceIndex * rowsPerPage) +
        startIncrement -
        footersBeforeMe;

      const myExtraRows = endIncrement - startIncrement;
      const isFooterDevice = enableFooter && groupEndFlatIndexes.includes(deviceIndex);
      const footerAdjustment = isFooterDevice ? 1 : 0;

      endIdx = startIdx + rowsPerPage + myExtraRows - footerAdjustment;

    } else {
      // ========== LINEAR LOGIC - FIXED FOR SYNCHRONIZED FLIPPING ==========
      // All devices flip together through the dataset
      // Example: 3 devices, 10 rows each, flip=1 (2 pages total)
      // Flip 0: Device 0 shows rows 0-9, Device 1 shows 10-19, Device 2 shows 20-29
      // Flip 1: Device 0 shows rows 30-39, Device 1 shows 40-49, Device 2 shows 50-59

      const cycleSize = totalDevices * rowsPerPage; // Total rows shown per flip
      const baseCycleOffset = flipIndex * cycleSize; // Starting row for this flip

      startIdx = baseCycleOffset + (deviceIndex * rowsPerPage);
      endIdx = startIdx + rowsPerPage;
    }

    setStartIndex(startIdx);
    setEndIndex(endIdx);

    setCurrentFlights(windowedFlights.slice(startIdx, endIdx));
  }, [
    windowedFlights,
    flipIndex,
    deviceIndex,
    rowsPerPage,
    startIncrement,
    endIncrement,
    displayLayoutsOrder,
    totalDevices,
    enableFooter,
    isLastChild,
  ]);

  useEffect(() => {
    if (!displayLayoutsOrder || displayLayoutsOrder.length === 0 || flip === 0) return;

    const isMatrix = Array.isArray(displayLayoutsOrder[0]);
    const totalDeviceGroups = isMatrix ? displayLayoutsOrder.length : 1;
    const flipsPerDevice = flip + 1;
    const totalPages = flipsPerDevice * totalDeviceGroups;

    if (totalPages === 1) {
      setCurrentPage(1);
      setFlipIndex(0);
      return;
    }

    // ✅ Use flipOffsetAtSeconds; fallback to 5s if not set/invalid
    const offsetSec = Number(flipOffsetAtSeconds);
    const periodSec = Number.isFinite(offsetSec) && offsetSec > 0 ? offsetSec : 5;

    // ✅ Calculate total cycle time
    const totalCycleSeconds = flipsPerDevice * periodSec;

    // ✅ Validation: Cycle must fit within 60 seconds AND divide evenly into 60
    if (totalCycleSeconds > 60 || 60 % totalCycleSeconds !== 0) {
      console.warn(
        `Invalid flip configuration: ${flipsPerDevice} flips × ${periodSec}s = ${totalCycleSeconds}s ` +
        `(must be ≤60s and 60 must divide evenly by ${totalCycleSeconds}s)`
      );
      setFlipIndex(0);
      return;
    }

    let clockCheckInterval: number | undefined;

    // ✅ Function to calculate flip index from global epoch time
    const calculateFlipFromClock = () => {
      const now = Date.now();
      const epochSeconds = Math.floor(now / 1000);
      const globalCyclePosition = epochSeconds % totalCycleSeconds;
      const calculatedFlipIndex = Math.floor(globalCyclePosition / periodSec);

      return {
        flipIndex: calculatedFlipIndex,
        epochSeconds,
        globalCyclePosition
      };
    };

    // ✅ Update flip index and page based on clock
    const syncWithClock = () => {
      const { flipIndex: newFlipIndex, epochSeconds, globalCyclePosition } = calculateFlipFromClock();

      setFlipIndex((currentFlipIndex) => {
        if (currentFlipIndex !== newFlipIndex) {

          setIsFading(true);
          window.setTimeout(() => {
            setCurrentPage((prev) => {
              let next = prev + 1;
              if (next > totalPages) next = 1;
              return next;
            });
            setIsFading(false);
          }, 500);
        }
        return newFlipIndex;
      });
    };

    // ✅ Initial sync - set the correct flip immediately
    const { flipIndex: initialFlipIndex, epochSeconds, globalCyclePosition } = calculateFlipFromClock();
    setFlipIndex(initialFlipIndex);
    setCurrentPage(initialFlipIndex + 1);

    // ✅ Check every second to stay synchronized with the global clock
    clockCheckInterval = window.setInterval(() => {
      if (flip === 0) return;
      syncWithClock();
    }, 1000); // Check every second

    return () => {
      if (clockCheckInterval) window.clearInterval(clockCheckInterval);
    };
    // ✅ DO NOT depend on currentPage or flights; timer shouldn't reset when they change
  }, [flip, displayLayoutsOrder, flipOffsetAtSeconds]);


  const fetchRules = async () => {
    try {
      const response = await getData<any[]>(apiRoutes.rules.getAllRules);
      if (response.success && response.data) setRules(response.data);
    } catch (err) {
      console.error("Failed to fetch rules:", err);
    }
  };

  const currentMoment = useMemo(() => moment(currentTime), [currentTime]);

  const checkRule = useCallback((flight: FlightDetails) => {
    const flightETA = moment(flight?.ETA);
    if (
      flight?.remarkCode?.key
      || currentMoment.isAfter(flightETA, 'minute')
    ) return null;
    return true;
  }, [currentMoment]);

  const getRuleRemark = useCallback((flight: FlightDetails): FlightStatus | undefined | null => {
    const flightETA = moment(flight?.ETA);

    const gateShownRule = rules.find(rule => rule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN);
    const goToGateRule = rules.find(rule => rule?.remarkCode?.key === RemarkToCodeMapping.GO_TO_GATE);

    if (!gateShownRule || !goToGateRule) return null;

    const gateShownTime = flightETA.clone().subtract(gateShownRule.hours * 60, 'minutes');
    const goToGateTime = flightETA.clone().subtract(goToGateRule.hours * 60, 'minutes');

    if (currentMoment.isBetween(gateShownTime, goToGateTime)) {
      return { key: RemarkToCodeMapping.GATE_SHOWN, description: `${gateShownRule?.remarkCode?.description} ${gateShownRule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN ? " at " + gateShownTime.format('HH:mm') : ""}` };
    } else if (currentMoment.isSameOrAfter(goToGateTime)) {
      if (flight.standCodeChanged) return { key: RemarkToCodeMapping.GATE_CHANGE, description: "Gate Change" };
      return { key: RemarkToCodeMapping.GO_TO_GATE, description: `${goToGateRule?.remarkCode?.description} ${goToGateRule?.remarkCode?.key === RemarkToCodeMapping.GATE_SHOWN ? " at " + goToGateTime.format('HH:mm') : ""}` };
    }

    return null;
  }, [currentMoment, rules]);

  useEffect(() => {
    if (!socket) return;
    socket.on("server_started", () => {
      console.log("server_started");
    });

    return () => {
      socket.off("server_started");
    };
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleBulkUpdate = (payload: {
      trigger: string;
      iataCode: string;
      cityName: string;
      translations: { lang: string; value: string }[];
      updatedFlightCount: number;
    }) => {
      console.log(`✈️ Bulk flight update — trigger: ${payload.trigger}, iataCode: ${payload.iataCode}, affected: ${payload.updatedFlightCount}`);

      setFlights(prevFlights =>
        prevFlights.map(flight => {
          const hasStation = flight.stations?.some(
            (s: any) => s.airport === payload.iataCode
          );
          if (!hasStation) return flight;

          return {
            ...flight,
            stations: flight.stations.map((station: any) => {
              if (station.airport !== payload.iataCode) return station;
              return {
                ...station,
                city: payload.cityName,
                translations: payload.translations,
              };
            }),
          };
        })
      );
    };

    socket.on('flights.station.update', handleBulkUpdate);

    return () => {
      socket.off('flights.station.update', handleBulkUpdate);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    const handleRemarkUpdate = (payload: {
      trigger: string;
      remarkCode: string;
      remarkName: string;
      airportLang: string;
      translations: { lang: string; value: string }[];
      updatedFlightCount: number;
    }) => {
      console.log(`✈️ Bulk remark update — trigger: ${payload.trigger}, remarkCode: ${payload.remarkCode}, affected: ${payload.updatedFlightCount}`);

      setFlights(prevFlights =>
        prevFlights.map(flight => {
          if (
            flight.remarkCode?.key !== payload.remarkCode &&
            flight.carouselRemarks?.key !== payload.remarkCode &&
            flight.checkInStatus?.key !== payload.remarkCode &&
            flight.currentStatus?.key !== payload.remarkCode
          ) return flight;

          // Backend already resolved: en + airport.language + all airlineLanguages + all stationLangs
          // Frontend filters down per-flight by its own stationLangs only
          const stationLangs = new Set(
            (flight.stations ?? []).flatMap((s: any) =>
              (s.translations ?? [])
                .filter((t: any) => t.lang !== 'en')
                .map((t: any) => t.lang)
            )
          );

          const filteredTranslations = payload.translations.filter(
            (t: any) => t.lang === 'en' || t.lang === flight?.airline?.language || t.lang === payload.airportLang || stationLangs.has(t.lang)
          );

          const patch: Partial<FlightDetails> = {};

          if (flight.remarkCode?.key === payload.remarkCode) {
            patch.remarkCode = { ...flight.remarkCode, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.carouselRemarks?.key === payload.remarkCode) {
            patch.carouselRemarks = { ...flight.carouselRemarks, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.checkInStatus?.key === payload.remarkCode) {
            patch.checkInStatus = { ...flight.checkInStatus, description: payload.remarkName, translations: filteredTranslations };
          }
          if (flight.currentStatus?.key === payload.remarkCode) {
            patch.currentStatus = { ...flight.currentStatus, description: payload.remarkName, translations: filteredTranslations };
          }

          return { ...flight, ...patch };
        })
      );
    };

    socket.on('flights.remark.update', handleRemarkUpdate);

    return () => {
      socket.off('flights.remark.update', handleRemarkUpdate);
    };
  }, [socket]);

  // Full refetch — triggered by service type changes etc.
  useEffect(() => {
    if (!socket) return;

    const handleRefetch = (payload: { trigger: string }) => {
      console.log(`🔄 Flights refetch triggered — reason: ${payload.trigger}`);
      fetchFlights();
    };

    socket.on('flights.refetch', handleRefetch);
    return () => { socket.off('flights.refetch', handleRefetch); };
  }, [socket]);

  const fetchFlights = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 5000, 10000]; // 2s, 5s, 10s

    try {
      const response = await getData<any[]>(apiRoutes.flightTrips.getFlightTrips);
      if (response.success && response.data) {
        setFlights(response.data as []);
        console.log(`✅ Flights fetched successfully${retryCount > 0 ? ` (after ${retryCount} retries)` : ''}`);
      } else {
        console.warn("Flight fetch returned unsuccessful response");
      }

      localStorage.setItem("fetchFlights", new Date().toString());
      setFlipIndex(0);
      setInitialLoadDone(true);
    } catch (err: any) {
      console.error(`❌ Fetch flights failed (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, err);

      // Determine if error is retryable
      const isRetryable =
        !err.response || // Network error
        err.code === 'ECONNABORTED' || // Timeout
        err.response?.status >= 500 || // Server error
        err.response?.status === 429 || // Too many requests
        err.response?.status === 408; // Request timeout

      const isLastAttempt = retryCount >= MAX_RETRIES;

      // Don't retry on auth errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.error("❌ Authentication error fetching flights");
        return;
      }

      // Retry if error is retryable and not last attempt
      if (isRetryable && !isLastAttempt) {
        const delay = RETRY_DELAYS[retryCount] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
        console.log(`⏳ Retrying fetchFlights in ${delay}ms...`);

        setTimeout(() => {
          fetchFlights(retryCount + 1);
        }, delay);
      } else {
        console.error("❌ Failed to fetch flights after all retries");
      }
    }
  };


  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(moment());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);


  const shouldKeepFlight = useCallback((item: FlightDetails, now = Date.now()) => {
    if (item.isDeleted || !item.isDisplay) return false;

    const isArrival = cluster?.filters?.tripType === TripType.Arrival;
    const CARGO_LANDED_OFFSET_MINUTES = 20;

    const key = item?.currentStatus?.key;
    const offsetMinutes = getOffsetIfRemovable(key);

    if (offsetMinutes !== undefined) {
      const effectiveTimeMs =
        (item as any)._cachedEffectiveTimeMs ??
        ((item as any)._cachedEffectiveTimeMs = new Date(item.effectiveTime).getTime());

      if (Number.isFinite(effectiveTimeMs)) {
        if (effectiveTimeMs + offsetMinutes * 60 * 1000 <= now) return false;
      }
    }

    if (isArrival && CARGO_SERVICE_TYPE_CODES.includes(item.serviceTypeCode)) {
      const isLanded = item?.remarkCode?.key === RemarkToCodeMapping.LANDED;
      if (isLanded) {
        const effectiveTimeMs =
          (item as any)._cachedEffectiveTimeMs ??
          ((item as any)._cachedEffectiveTimeMs = new Date(item.effectiveTime).getTime());

        if (Number.isFinite(effectiveTimeMs)) {
          return effectiveTimeMs + CARGO_LANDED_OFFSET_MINUTES * 60 * 1000 > now;
        }
      }
    }

    return true;
  }, [cluster?.filters?.tripType, remarkOffsets]);

  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const filterFlights = () => {
      const now = Date.now();

      setFlights((prevFlights) => {
        const filtered = prevFlights.filter((item) => shouldKeepFlight(item, now));
        return filtered.length !== prevFlights.length ? filtered : prevFlights;
      });
    };

    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);

    const timeoutId = setTimeout(() => {
      filterFlights();
      const intervalId = setInterval(filterFlights, 60_000);
      (timeoutRef as any).current = intervalId;
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeoutId);
      clearInterval((timeoutRef as any).current);
    };
  }, [shouldKeepFlight]);

  const getHeaderImageSrc = () => {
    switch (cluster.displayType) {
      case DisplayType.MultiUserArrival:
        return "/arrival-header.bmp";
      case DisplayType.MultiUserDeparture:
        return "/departure-header.bmp";
      case DisplayType.MultiUserBaggage:
        return "/baggage-header.bmp";
      case DisplayType.MultiUserCheckIn:
        return "/checkin-header.bmp";
      default:
        return "/default-header.bmp";
    }
  }

  // const getFontSize = (width: number): string => {
  //   if (width < 400) return '12px';
  //   else if (width < 576) return '14px';
  //   else if (width < 768) return '16px';
  //   else if (width < 992) return '20px';
  //   else if (width < 1200) return '24px';
  //   else if (width < 1400) return '27px';
  //   else if (width <= 1800) return '30px';
  //   else if (width <= 2560) return '34px';
  //   else return '40px';
  // };

  const getLogoSize = (width: number): { width: number; height: number } => {
    const baseWidth = 180;
    const baseHeight = 45;

    if (width < 400) return { width: Math.round(baseWidth * 0.5), height: Math.round(baseHeight * 0.5) };
    else if (width < 576) return { width: Math.round(baseWidth * 0.6), height: Math.round(baseHeight * 0.6) };
    else if (width < 768) return { width: Math.round(baseWidth * 0.7), height: Math.round(baseHeight * 0.7) };
    else if (width < 992) return { width: Math.round(baseWidth * 0.8), height: Math.round(baseHeight * 0.8) };
    else if (width < 1200) return { width: Math.round(baseWidth * 0.85), height: Math.round(baseHeight * 0.85) };
    else if (width < 1400) return { width: Math.round(baseWidth * 0.9), height: Math.round(baseHeight * 0.9) };
    else if (width <= 1800) return { width: baseWidth, height: baseHeight };
    else if (width <= 2560) return { width: Math.round(baseWidth * 1.15), height: Math.round(baseHeight * 1.15) };
    else return { width: Math.round(baseWidth * 1.3), height: Math.round(baseHeight * 1.3) };
  };

  const updateSizing = () => {
    const width = window.innerWidth;
    const newLogoSize = getLogoSize(width);
    setScreenWidth(width);
    setLogoSize(newLogoSize);
  };

  useEffect(() => {
    updateSizing();

    // Debounce resize events
    let timeoutId: number;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(updateSizing, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);


  // Max height for the table wrapper, floored to a whole number of rows so no row is ever half-visible.
  // screenWidth as dep ensures it recalculates on window resize.
  const tableClipHeight = useMemo(() => {
    const available =
      window.innerHeight
      - (isChild ? 0 : (headerHeight || 0))
      - (footerHeight || 0)
      - (tableHeaderHeight || 0);
    const wholeRows = Math.floor(available / (rowHeight || 1));
    return wholeRows * (rowHeight || 0) + (tableHeaderHeight || 0);
  }, [screenWidth, isChild, headerHeight, footerHeight, tableHeaderHeight, rowHeight]);

  // Calculate displayed flights based on current ad ratio
  const displayedFlights = useMemo(() => {
    if (!currentAd?.ratio) return currentFlights;
    const count = Math.ceil((((100 - currentAd.ratio) / 100) * currentFlights.length) - 1);
    return currentFlights.slice(0, count);
  }, [currentFlights, currentAd?.ratio]);


  // Detect changes in displayedFlights and show loading indicator
  useEffect(() => {
    // Check if flipIndex changed (read current value directly)
    const currentFlipIndex = flipIndex;
    const isFlipChange = currentFlipIndex !== prevFlipIndexRef.current;

    // If flip changed, update ref and exit WITHOUT checking data
    if (isFlipChange) {
      prevFlipIndexRef.current = currentFlipIndex;
      prevDisplayedFlightsRef.current = displayedFlights;
      return; // Exit early - don't check for data changes
    }

    // Skip on initial mount
    if (prevDisplayedFlightsRef.current.length === 0 && displayedFlights.length > 0) {
      prevDisplayedFlightsRef.current = displayedFlights;
      return;
    }

    if (displayedFlights.length === 0) {
      prevDisplayedFlightsRef.current = [];
      return;
    }

    // Only check for data changes if NOT a flip
    const prevIds = new Set(prevDisplayedFlightsRef.current.map(f => f._id));
    const currentIds = new Set(displayedFlights.map(f => f._id));

    const added = Array.from(currentIds).filter(id => !prevIds.has(id));
    const removed = Array.from(prevIds).filter(id => !currentIds.has(id));

    // Show loading only for actual data changes (not flip)
    if (added.length > 0 || removed.length > 0) {
      console.log(`📊 Displayed flights changed: +${added.length} added, -${removed.length} removed`);

      setIsDataChanging(true);

      setTimeout(() => {
        setIsDataChanging(false);
      }, 1500);
    }

    // Update ref
    prevDisplayedFlightsRef.current = displayedFlights;
  }, [displayedFlights]); // ✅ ONLY depend on displayedFlights, NOT flipIndex


  return (
    <div>
      {/* Full screen advertisement */}
      <AdvertisementDisplay advertisement={currentAd} isFullScreen={true} />

      {defaultNoFlightsUI}
      {/* Regular flight display with table */}
      {!showDefaultUI && (!currentAd || currentAd?.ratio || currentAd?.advertisementType !== 'FullScreen') && (
        <div style={{ paddingBottom: footerHeight }}>
          {!isChild && (
            <div style={{ position: 'relative', height: headerHeight }} className="col-md-12">
              <img
                src={getHeaderImageSrc()}
                style={{ height: `${headerHeight}px`, padding: 0, margin: 0, width: '100%' }}
              />
              <h5
                style={{
                  position: 'absolute',
                  fontSize: fontSize,
                  bottom: '1rem',
                  right: '1rem',
                  zIndex: 10
                }}
                className='mb-0 text-white'
              >
                {moment(currentTime).format('dddd DD MMMM YYYY')}
                <span className='ms-3'>{moment(currentTime).format('HH:mm')}</span>
              </h5>
              {/* Page indicator - only show if flip > 0 */}
              {flip > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    fontSize: `calc(${fontSize} * 0.9)`, //TODO
                    bottom: '1rem',
                    left: '50%',
                    zIndex: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.5rem',
                  }}
                  className='text-white'
                >
                  {flipIndex + 1}/{flip + 1}
                </div>
              )}
            </div>
          )}

          {/* Loading Indicator Overlay - Shows when displayedFlights changes */}

          <div style={{
            overflow: 'hidden',
            maxHeight: tableClipHeight,
          }}>
          <table
            style={{ width: "100%", fontSize: fontSize }}
            className={`w-full text-center text-white ${(isFading || isDataChanging) ? 'shimmer' : ''}`}
          >
            {(
              <thead className={isChild ? "thead-ghost" : ""} style={{ height: tableHeaderHeight }}>
                <tr style={{ border: "1px solid" }}>
                  {template?.columns?.map((col: TemplateColumn) => (
                    <th
                      key={col.field}
                      className={col.header?.cssClass || ""}
                      style={{
                        fontSize: fontSize,
                        ...col.header?.inLineStyle?.reduce((styleObj: any, style) => {
                          const camelCaseProp = toCamelCase(style.prop);
                          styleObj[camelCaseProp] = style.value;
                          return styleObj;
                        }, {}),
                      }}
                    >
                      {cluster?.filters?.tripType === TripType.Departure && col.header?.label === "From" ? "To" : cluster?.filters?.tripType === TripType.Arrival && col.header?.label === "To" ? "From" : col.header?.label || ""}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {displayedFlights.map((flight: any, index) => (
                <tr
                  style={{
                    backgroundColor: index % 2 == 0 ? template.alternateRow.background : "initial",
                    color: index % 2 == 0 ? template.alternateRow.color : "initial",
                    height: rowHeight,
                    // fontWeight: 'bold' // TODO

                  }}
                  key={index}
                  className="odd:bg-gray-600 even:bg-gray-700"
                >
                  {template?.columns?.map((col: any) => {
                    let cellContent;

                    switch (col.field) {
                      case "ETA": {
                        const remarkKey = flight?.remarkCode?.key;

                        const isDepartedOrLanded =
                          remarkKey === RemarkToCodeMapping.DEPARTED ||
                          remarkKey === RemarkToCodeMapping.LANDED;

                        const value = isDepartedOrLanded
                          ? (flight?.actualArrivalDepartureTime || flight?.ETA)
                          : flight?.ETA;

                        const show = value ? (value === flight?.scheduledTravelTime ? false : true) : false;

                        cellContent = (
                          value &&
                          show &&
                          <span
                            style={(col.body?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                              const camelCaseProp = toCamelCase(style.prop);
                              if (camelCaseProp !== "backgroundColor") styleObj[camelCaseProp] = style.value;
                              if (camelCaseProp === "color") styleObj[camelCaseProp] = index % 2 == 0 ? template?.alternateRow?.color : style.value;
                              return styleObj;
                            }, {})}
                          >
                            <span style={{ display: "block", lineHeight: 1.2 }}> {moment(value).utcOffset(timezone).format(col.body?.time?.format || "HH:mm")} </span>
                            <small style={{ display: "block", lineHeight: 1.2, marginTop: "-10px", marginLeft: "5px" }}>
                              {
                                moment(value).utcOffset(timezone).isSame(moment(), 'day')
                                  ? ''
                                  : (
                                    <small>
                                      <small
                                        style={{
                                          ...(col.body?.date?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                                            const camelCaseProp = toCamelCase(style.prop);
                                            styleObj[camelCaseProp] = style.value;
                                            return styleObj;
                                          }, {}),
                                        }}
                                      >{moment(value).utcOffset(timezone).format(col.body?.date?.format || "DD MMM")}</small>
                                    </small>
                                  )}
                            </small>
                          </span>
                        );
                        break;
                      }
                      case "counterOpeningTime":
                      case "counterClosingTime":
                      case "loungeOpeningTime":
                      case "loungeClosingTime":
                      case "loungeBoardingTime":
                      case "finalLoungeTime":
                      case "lastBagReceivedAt":
                      case "scheduledTravelTime":
                      case "officialFlightDate":
                        const show = col.field === "ETA" ? (flight.ETA === flight.scheduledTravelTime ? false : true) : true;
                        cellContent = (
                          flight[col.field] &&
                          show &&
                          <span
                            style={(col.body?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                              const camelCaseProp = toCamelCase(style.prop);
                              if (camelCaseProp !== "backgroundColor") styleObj[camelCaseProp] = style.value;
                              if (camelCaseProp === "color") styleObj[camelCaseProp] = index % 2 == 0 ? template?.alternateRow?.color : style.value;
                              return styleObj;
                            }, {})}
                          >
                            <span style={{ display: "block", lineHeight: 1.2 }}> {moment(flight[col.field]).utcOffset(timezone).format(col.body?.time?.format || "HH:mm")} </span>
                            <small style={{ display: "block", lineHeight: 1.2, marginTop: "-10px", marginLeft: "5px" }}>
                              {
                                moment(flight[col.field]).utcOffset(timezone).isSame(moment(), 'day')
                                  ? ''
                                  : (
                                    <small>
                                      <small
                                        style={{
                                          ...(col.body?.date?.inLineStyle || []).reduce((styleObj: any, style: any) => {
                                            const camelCaseProp = toCamelCase(style.prop);
                                            styleObj[camelCaseProp] = style.value;
                                            return styleObj;
                                          }, {}),
                                        }}
                                      >{moment(flight[col.field]).utcOffset(timezone).format(col.body?.date?.format || "DD MMM")}</small>
                                    </small>
                                  )}
                            </small>
                          </span>
                        );
                        break;
                      case "airline":
                        cellContent = (
                          <AirlineCell
                            flightNumber={flight.flightNumber}
                            codeShareFlights={flight.codeShareFlight || []}
                            logoSize={logoSize}
                            rowHeight={rowHeight!}
                            globalStartTime={globalStartTime}
                          />
                        );
                        break;
                      case "flightNumber":
                        cellContent = (
                          <div>
                            <FlightNumberCell
                              // logoSize={logoSize}
                              flight={flight}
                              globalStartTime={globalStartTime}
                              rowHeight={rowHeight!}
                              // showLogo={col?.body?.showLogo}
                              showInline={col?.body?.showFlightsInline}
                            />
                          </div>
                        );
                        break;
                      case "stations":
                        let stations;

                        if (flight.tripType === TripType.Arrival) {
                          stations = flight.stations.slice(0, -1).map((station: { translations: any[]; }) => filterPosAviationStationLangs(station.translations).map(t => t.value)
                          ).flat();
                        } else if (flight.tripType === TripType.Departure) {
                          stations = flight.stations.slice(1).map((station: { translations: any[]; }) => filterPosAviationStationLangs(station.translations).map(t => t.value)
                          ).flat();
                        }
                        let mainFlightNumber = flight.flightNumber;
                        cellContent = (
                          <div>
                            {stations.length > 0 && (
                              <StationsRotator
                                lastIndex={-1}
                                globalStartTime={globalStartTime}
                                items={stations}
                                mainFlightNumber={mainFlightNumber}
                              />
                            )}
                          </div>
                        );
                        break;
                      case "standCode":
                        cellContent = (
                          <span className="p-1"> {cluster?.filters?.isPublic ? (getRuleRemark(flight)?.key === RemarkToCodeMapping.GATE_SHOWN ? "" : normalizeCode(flight?.loungeCode)) : flight.standCode}</span>
                        );
                        break;
                      case "checkInType":
                        cellContent = (
                          <span className="p-1"> {flight?.checkInType?.description}</span>
                        );
                        break;
                      case "terminalId":
                        cellContent = (
                          <span className="p-1"> {flight?.terminal?.name}</span>
                        );
                        break;
                      case "loungeCode":
                        cellContent = (
                          <span className="p-1"> {cluster?.filters?.isPublic ? normalizeCode(flight.loungeCode)?.replace(/[LR]$/, '') : normalizeCode(flight.loungeCode)?.replace(/[LR]$/, '')}</span>
                        );
                        break;
                      case "remarkCode":
                        const specificStyles = col.body?.specificStyles?.[flight?.remarkCode?.key] || {};
                        let remarksList: string[] = [];
                        if (cluster?.displayType === DisplayType.MultiUserBaggage) {
                          if (flight?.currentStatus) {
                            const shouldIncludeRemarks = !isPosAviationHiddenRemark(flight?.currentStatus?.key) && !(
                              cluster?.filters?.isPublic &&
                              flight.ETA &&
                              checkRule(flight)
                            );

                            const remarks = Array.isArray(flight?.currentStatus?.translations) && shouldIncludeRemarks
                              ? filterPosAviationLangs(flight.currentStatus.translations).map((item: { value: any }) => item.value)
                              : [];

                            remarks.forEach((value: string) => remarksList.push(value));
                          }
                        }
                        else if (cluster?.displayType === DisplayType.MultiUserCheckIn) {
                          const shouldIncludeRemarks = !(
                            cluster?.filters?.isPublic &&
                            flight.ETA &&
                            checkRule(flight)
                          );

                          let checkInStatus: string[] = [];
                          let flightRemarks: string[] = [];

                          const checkInTranslations = Array.isArray(flight?.checkInStatus?.translations) && shouldIncludeRemarks
                            ? filterPosAviationLangs(flight.checkInStatus.translations).map((item: { value: any }) => item.value)
                            : [];

                          const remarkTranslations = Array.isArray(flight?.remarkCode?.translations) && shouldIncludeRemarks
                            && !isPosAviationHiddenRemark(flight?.remarkCode?.key)
                            ? filterPosAviationLangs(flight.remarkCode.translations).map((item: { value: any }) => item.value)
                            : [];

                          if (flight?.remarkCode?.key === RemarkToCodeMapping.CANCELLED) {
                            flightRemarks = remarkTranslations;
                          } else if (flight?.remarkCode?.key === RemarkToCodeMapping.DELAYED) {
                            flightRemarks = [...remarkTranslations, ...checkInTranslations];
                          } else {
                            checkInStatus = checkInTranslations;
                          }

                          const finalRemarks = [...flightRemarks, ...checkInStatus];
                          finalRemarks.forEach((value: string) => remarksList.push(value));
                        }
                        else {
                          if (flight?.remarkCode) {
                            const shouldIncludeRemarks = !isPosAviationHiddenRemark(flight?.remarkCode?.key) && !(
                              cluster?.filters?.isPublic &&
                              flight.ETA &&
                              checkRule(flight)
                            );

                            const remarks = Array.isArray(flight?.remarkCode?.translations) && shouldIncludeRemarks
                              ? filterPosAviationLangs(flight.remarkCode.translations).map((item: { value: any }) => item.value)
                              : [];

                            remarks.forEach((value: string) => remarksList.push(value));
                          }
                        }

                        cellContent = (
                          <div
                            className={"p-1"}
                            style={(specificStyles?.inLineStyle || []).reduce(
                              (styleObj: any, style: any) => {
                                styleObj[style.prop] = style.value;
                                return styleObj;
                              },
                              {}
                            )}
                          >
                            <div>
                              {remarksList.length > 0 && (
                                <RemarksRotator
                                  globalStartTime={globalStartTime}
                                  mainFlightNumber={flight.flightNumber}
                                  items={remarksList}
                                />
                              )}
                            </div>
                          </div>
                        );
                        break;
                      case "checkInRow":
                        const checkInValue = flight?.checkInType?.description === CheckInType.D
                          ? flight?.checkInDeskRange
                          : flight?.checkInRowsRange;

                        cellContent = (
                          <span className="position-relative overflow-hidden d-inline-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                            <span
                              className={`d-inline-block text-nowrap ${checkInValue && checkInValue.length > 3 ? 'animate-scroll-left' : ''}`}
                            >
                              {checkInValue}
                            </span>
                          </span>
                        );
                        break;
                      default:
                        cellContent = flight[col.field] || "";
                    }

                    return (
                      <td
                        key={col.field}
                        className={col.body?.cssClass || ""}
                        style={{
                          fontSize: fontSize,
                          ...col.body?.inLineStyle?.reduce((styleObj: any, style: any) => {
                            const camelCaseProp = toCamelCase(style.prop);
                            if (index % 2 == 0 && camelCaseProp === "color") {
                              styleObj[camelCaseProp] = template?.alternateRow?.color || style.value;
                            }
                            else if (index % 2 == 0 && camelCaseProp === "backgroundColor") {
                              styleObj[camelCaseProp] = template?.alternateRow?.background || style.value;
                            }
                            else styleObj[camelCaseProp] = style.value;
                            return styleObj;
                          }, {}),
                        }}
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Ratio-based advertisement */}
          <AdvertisementDisplay advertisement={currentAd} isFullScreen={false} />

          {/* Footer */}
          <div className="font-semibold fix-bottom" style={{ height: footerHeight }}>
            {(!isMatrix && enableFooter || (isMatrix && isLastChild)) && (
              <div>
                <AnnouncementRenderer
                  announcement={announcement}
                  footerHeight={footerHeight}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateComponent;